import React from "react";
import socket_io from "socket.io-client"
import { createContext, useCallback, useEffect, useState, useRef, useContext, useLayoutEffect } from "react"
import useJwt from "@/utils/jwt/useJwt"
import { useDispatch, useSelector } from "react-redux"
import { getRoomList, insertRoomUser, calculateUnSeenCount, updateRoomLastMessage } from "@/store/actions/room"
import { setSocketConnection } from "@/store/actions/user";
import { getMessages, reduxDeleteMessages, reduxInsertMessages, reduxUpdateMessages, receiveMessages, notifyMessage } from "@/store/actions/messages"
import OneSignal from 'react-onesignal';
import { isMessageSeen, nowSecs, randomString, getUserDisplayName, normalizeMessage, normalizeMessages } from "@/utils/common"
import { getLocalRoomMessages } from "@/utils/db/indexedDB";
import { useLocation } from "react-router-dom";

import { handleLogout } from "@/store/actions/auth";
import { LoaderContext } from "@/utils/context/ProgressLoader";

const SocketContext = createContext()

const SocketProvider = ({ children }) => {

  const dispatch = useDispatch();
  const room = useSelector((state) => state.room);
  const users = useSelector((state) => state.users.connected_users);
  const [opponentTyping, setOpponentTyping] = useState({});
  const [onlineUsers, setOnlineUsers] = useState([]);
  const auth = useSelector(state => state.auth)
  const { showProgress, hideProgress } = useContext(LoaderContext);

  const location = useLocation();

  const isChatPage = location.pathname.startsWith("/chat");

  useLayoutEffect(() => {
    if (isChatPage) {
      showProgress();
    }
  }, [isChatPage, showProgress]);

  useEffect(() => {
    let timer;

    if (isChatPage) {
      timer = setTimeout(() => {
        hideProgress();
      }, 2000);
    } else {
      hideProgress();
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isChatPage, hideProgress]);

  const socket = useRef(null);
  const selectedRoomIdRef = useRef(null);

  useEffect(() => {
    selectedRoomIdRef.current = room?.selectedRoom?.id ?? null;
  }, [room?.selectedRoom?.id]);

  useEffect(() => {
    const token = auth?.accessToken;

    if (!token) {
      return;
    }

    const newSocket = socket_io.connect(
      process.env.REACT_APP_SOCKET_URL,
      {
        auth: { token },

        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        timeout: 20000,

        autoConnect: false,
      }
    );

    socket.current = newSocket;

    // 防止旧 Socket 修改当前连接状态
    const isCurrentSocket = () => socket.current === newSocket;

    const setDisconnected = (value) => {
      if (isCurrentSocket()) {
        dispatch(setSocketConnection(value));
      }
    };

    const syncCurrentData = () => {
      if (!isCurrentSocket()) {
        return;
      }

      dispatch(getRoomList());

      const selectedRoomId = selectedRoomIdRef.current;

      if (selectedRoomId) {
        dispatch(
          getMessages({
            id: selectedRoomId,
            limit: 50,
          })
        );
      }
    };

    const handleConnected = () => {
      if (!isCurrentSocket()) {
        return;
      }

      console.log("Socket connected:", newSocket.id);

      setDisconnected(false);

      newSocket.emit("login", { token });

      syncCurrentData();
    };

    const handleDisconnected = (reason, details) => {
      if (!isCurrentSocket()) {
        return;
      }

      console.log("Socket disconnected:", reason);
      console.log("Disconnect details:", details);

      setDisconnected(true);

      if (reason === "io server disconnect" && navigator.onLine) {
        setTimeout(() => {
          if (
            isCurrentSocket() &&
            !newSocket.connected
          ) {
            newSocket.connect();
          }
        }, 2000);
      }
    };

    const handleConnectError = (error) => {
      if (!isCurrentSocket()) {
        return;
      }

      console.error("Socket connection error:", error);

      setDisconnected(true);

      console.log("Will auto reconnect:", newSocket.active);
    };

    const handleReconnectAttempt = (attempt) => {
      if (!isCurrentSocket()) {
        return;
      }

      console.log("Socket reconnecting:", attempt);

      setDisconnected(true);
    };

    const handleOnline = () => {
      if (!isCurrentSocket()) {
        return;
      }

      console.log("Browser back online");

      if (!newSocket.connected) {
        newSocket.connect();
      }
    };

    const handleOffline = () => {
      if (!isCurrentSocket()) {
        return;
      }

      console.log("Browser offline");

      setDisconnected(true);
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        navigator.onLine &&
        !newSocket.connected &&
        isCurrentSocket()
      ) {
        console.log("Page active, checking socket");

        newSocket.connect();
      }
    };

    newSocket.on("connect", handleConnected);
    newSocket.on("disconnect", handleDisconnected);
    newSocket.on("connect_error", handleConnectError);

    newSocket.io.on(
      "reconnect_attempt",
      handleReconnectAttempt
    );

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    newSocket.connect();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      newSocket.off("connect", handleConnected);
      newSocket.off("disconnect", handleDisconnected);
      newSocket.off("connect_error", handleConnectError);

      newSocket.io.off(
        "reconnect_attempt",
        handleReconnectAttempt
      );

      newSocket.disconnect();

      if (socket.current === newSocket) {
        socket.current = null;
      }
    };
  }, [auth?.accessToken, dispatch]);

  const loadOnlineList = useCallback(() => {
    useJwt
      .getOnlineList()
      .then((res) => {
        if (res.data.ResponseCode === 0) {
          setOnlineUsers(res.data.ResponseResult);
        }
      })
      .catch(console.error)
  }, []);

  const getRoomOnlineStatus = useCallback(
    (room) => {
      if (!room?.room_users?.length) return false;

      return room.room_users.some((u) =>
        onlineUsers.some(
          (online) =>
            Number(u.user_id) !== Number(useJwt.getUserID()) &&
            u.user_id === online.user_id
        )
      );
    },
    [onlineUsers]
  );

  const reGetRommList = useCallback(() => {
    dispatch(getRoomList());
  }, [dispatch]);

  const socketSendTyping = (room_id, typing) => {
    socket.current.emit("sendTyping", {
      token: auth.accessToken,
      room_id: room_id,
      type: typing, // 1: typing, 0: stopped typing
    });
  };

  const socketSendMessage = (room_id, type, message, reply_on, forward_message, description) => {
    const selectedChat = room;
    if (!selectedChat) return;

    let local_id = randomString();
    let userData = auth?.userData;
    if (!userData) {
      dispatch(handleLogout())
    }
    // console.log(socket.current, 'send message')
    let newMessage = {
      user_id: userData.id,
      username: getUserDisplayName(userData),
      token: auth.accessToken,
      room_id,
      local_id,
      type, // 0: text, 1: image, 2: file
      message,
      description,
      created_at: nowSecs(),
      updated_at: nowSecs(),
      reply_on,
      forward_message,
    };
    // console.log(newMessage)
    // console.log("socket emit id:", socket.current?.id);
    socket.current.emit("sendMessage", newMessage);
  };

  const socketUpdateMessage = (message, messageText) => {
    if (!message) return;

    let userData = auth?.userData;
    if (!userData) {
      dispatch(handleLogout())
    }

    let updateMessage = {
      user_id: userData.id,
      username: getUserDisplayName(userData),
      token: auth.accessToken,
      room_id: message.room_id,
      message_id: message.id,
      message: messageText,
    };

    // console.log('update message', updateMessage);
    socket.current.emit("updateMessage", updateMessage);
  };

  const socketOpenMessage = useCallback((message_id) => {
    // console.log('socketOpenMessage', message_id);
    if (!socket.current) return;
    socket.current.emit("openMessage", {
      token: auth.accessToken,
      message_id: message_id,
    });
  }, [auth.accessToken]);

  const socketDeleteMessage = (message) => {
    if (!message) return;

    const { userData, accessToken } = auth || {};

    if (!userData) return;

    socket.current.emit("deleteMessage", {
      token: accessToken,
      user_id: userData.id,
      username: getUserDisplayName(userData),
      room_id: message.room_id,
      message_id: message.id
    });
  };


  const handleSocketNewUser = useCallback(
    (newUser) => {
      setOnlineUsers((prevUsers) => {
        const exists = prevUsers.some(
          (room_user) =>
            room_user.room_id === newUser.room_id && room_user.user_id === newUser.user_id
        );

        if (!exists) {
          return [...prevUsers, newUser];
        }
        return prevUsers;
      });

    },
    []
  );

  const handleSocketUserLeft = useCallback(
    (userLeft) => {
      if (!userLeft) return;

      setOnlineUsers((prevUsers) =>
        prevUsers.filter((room_user) => room_user.room_id !== userLeft.room_id || room_user.user_id !== userLeft.user_id)
      );
    },
    []
  );

  const getUser = useCallback(
    (user_id) => {
      if (!users) return null;

      for (let user of users) {
        if (user.user_id === user_id) {
          return user;
        }
      }

      return null;
    },
    [users]
  );

  const updateTyping = useCallback((room_id, user_id, typing) => {
    const user = getUser(user_id);
    if (user) {
      let ot = { ...opponentTyping };
      if (typing) {
        if (!ot[room_id] || ot[room_id].length === 0) {
          ot[room_id] = [user_id];
        } else {
          if (ot[room_id].indexOf(user_id) === -1) {
            ot[room_id].push(user_id);
          }
        }
      } else {
        if (ot[room_id]) {
          let idx = ot[room_id].indexOf(user_id);
          if (idx !== -1) ot[room_id].splice(idx, 1);
        }
      }
      setOpponentTyping(ot);
    }
  }, [opponentTyping, getUser]);

  const handleSocketTyping = useCallback(
    (typing) => {
      // received typing
      // console.log('typing', typing)

      if (Number(typing.user_id) !== Number(useJwt.getUserID())) {
        updateTyping(typing.room_id, typing.user_id, typing.type === 1);
      }
    },
    [updateTyping]
  );

  const processStoreInsert = useCallback(
    (messages, action) => {
      return dispatch(action(messages));
    },
    [dispatch]
  );

  const processMessages = useCallback(
    async (messages, action) => {
      const normalizedMessages = normalizeMessages(messages);
      if (normalizedMessages.length === 0) return;

      dispatch(updateRoomLastMessage(normalizedMessages));
      dispatch(calculateUnSeenCount());

      const { userData } = auth || {};
      const targetRoomId = normalizedMessages[0]?.room_id;

      if (!userData?.id || !targetRoomId) return;

      const selectedRoomId = room.selectedRoom?.id;
      const isSelectedRoom = Number(selectedRoomId) === Number(targetRoomId);
      const cached = await getLocalRoomMessages(userData.id, targetRoomId);

      if (!isSelectedRoom && !cached?.initialized) return;

      await processStoreInsert(normalizedMessages, action);

    },
    [auth, dispatch, processStoreInsert, room.selectedRoom]
  );

  const addMessages = useCallback(
    async (messages) => {
      await processMessages(messages, reduxInsertMessages);
    },
    [processMessages]
  );

  const updateMessages = useCallback(
    async (messages) => {
      await processMessages(messages, reduxUpdateMessages);

      const selectedChat = { ...room.selectedRoom };
      if (selectedChat && Object.keys(selectedChat).length > 0) {
        if (selectedChat.id === messages[0].room_id) {
          const unreadMessageIds = messages
            .filter((message) => !isMessageSeen(message))
            .map((message) => message.id);
          socketOpenMessage(unreadMessageIds);
          // console.log('open messages', messages);
        }
      }
    },
    [processMessages, room.selectedRoom, socketOpenMessage]
  );

  const handleSocketNewMessage = useCallback(
    (message) => {
      const normalizedMessage = normalizeMessage(message);
      // console.log('new message', message);
      if (Number(normalizedMessage.user_id) === Number(useJwt.getUserID())) {
        // console.log('updateMessages', message);

        updateMessages([normalizedMessage])
      }
      else {
        // console.log('new message', message);
        addMessages([normalizedMessage]);
        dispatch(receiveMessages())
        dispatch(notifyMessage(normalizedMessage))
      }
    },
    [addMessages, dispatch, updateMessages]
  );

  const handleSocketUpdateMessage = useCallback(
    (messages) => {
      // updated message
      // console.log('updated messages', messages)

      updateMessages(normalizeMessages(messages));
    }, [updateMessages]
  );

  const openMessage = useCallback(
    (message) => {
      dispatch(insertRoomUser(message));
    },
    [dispatch]
  );

  const handleSocketOpenMessage = useCallback(
    (message) => {
      // console.log('open message', message);
      openMessage(message);
    }, [openMessage]
  );

  const deleteMessages = useCallback(
    (message_ids) => {
      dispatch(reduxDeleteMessages(message_ids));
    },
    [dispatch]
  );

  const handleSocketDeleteMessage = useCallback(
    (message_id) => {
      // deleted message
      console.log('deleted messages', message_id)
      deleteMessages([message_id])
    },
    [deleteMessages]
  );

  useEffect(() => {
    const socketEvents = {
      newUser: handleSocketNewUser,
      userLeft: handleSocketUserLeft,
      typing: handleSocketTyping,
      newMessage: handleSocketNewMessage,
      updateMessage: handleSocketUpdateMessage,
      openMessage: handleSocketOpenMessage,
      deleteMessage: handleSocketDeleteMessage,
    };

    Object.entries(socketEvents).forEach(([event, handler]) => {
      socket.current?.on(event, handler);
    });

    return () => {
      Object.entries(socketEvents).forEach(([event, handler]) => {
        socket.current?.off(event, handler);
      });
    };
  }, [
    handleSocketNewUser,
    handleSocketUserLeft,
    handleSocketTyping,
    handleSocketNewMessage,
    handleSocketUpdateMessage,
    handleSocketDeleteMessage,
    handleSocketOpenMessage,
  ]);

  function foregroundWillDisplayListener(notification) {
    console.log(`notification will display: ${notification}`);
  }

  function promptListener(event) {
    console.log(`permission prompt dispslayed event: ${event}`);
  }

  function permissionChangeListener(permission) {
    if (permission) {
      console.log(`permission accepted!`);
    }
  }

  useEffect(() => {
    const isNotProduction = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_BASENAME !== 'live';

    if (isNotProduction) return;

    if (OneSignal.Notifications) {
      OneSignal.Notifications.addEventListener("foregroundWillDisplay", foregroundWillDisplayListener);
      OneSignal.Notifications.addEventListener("permissionPromptDisplay", promptListener);
      OneSignal.Notifications.addEventListener("permissionChange", permissionChangeListener);
    }
  }, [])

  return (
    <SocketContext.Provider value={{
      opponentTyping,
      getRoomOnlineStatus,
      addMessages,
      socketOpenMessage,
      socketSendTyping,
      socketSendMessage,
      socketUpdateMessage,
      socketDeleteMessage,
      loadOnlineList,
      reGetRommList,
    }}>{children}
    </SocketContext.Provider>
  );
};

export { SocketContext, SocketProvider }
